import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReceivedBookingsvarPanel } from '@/components/medcom/ReceivedBookingsvarPanel';
import { ReceivedEpikriserPanel } from '@/components/medcom/ReceivedEpikriserPanel';
import { ReceivedGgopPanel } from '@/components/ggop/ReceivedGgopPanel';
import { ReceivedXdis16Panel } from '@/components/xdis/ReceivedXdis16Panel';
import { XdisFlowPanel } from '@/components/xdis/XdisFlowPanel';
import { Button } from '@/components/ui/Button';
import { PatientCareCommunicationTab } from '@/components/patient/PatientCareCommunicationTab';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';

interface PatientKorrespondanceTabProps {
  clinicId: string;
  patientId: string;
  firstName: string;
  lastName: string;
  hasCpr: boolean;
  canRevealCpr: boolean;
  canWriteGgop?: boolean;
  canViewReferrals: boolean;
  canViewGgop: boolean;
  canViewXdis: boolean;
  canAttach: boolean;
  onGgop?: () => void;
}

/**
 * Frame-only wrapper for the Korrespondance tab. Panel fields, order, fetch and
 * CareCommunication/FHIR/send logic stay in the existing child components.
 */
export function PatientKorrespondanceTab({
  clinicId,
  patientId,
  firstName,
  lastName,
  hasCpr,
  canRevealCpr,
  canWriteGgop,
  canViewReferrals,
  canViewGgop,
  canViewXdis,
  canAttach,
  onGgop,
}: PatientKorrespondanceTabProps) {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const [composing, setComposing] = useState(false);
  const createRef = useRef<() => void>(() => undefined);
  const patientName = [firstName, lastName].filter(Boolean).join(' ') || null;

  const letters = (
    <PatientCareCommunicationTab
      clinicId={clinicId}
      patientId={patientId}
      firstName={firstName}
      lastName={lastName}
      hasCpr={hasCpr}
      canRevealCpr={canRevealCpr}
      hideCreateButton
      onRegisterCreate={(open) => {
        createRef.current = open;
      }}
      onCountChange={setCount}
      onComposingChange={setComposing}
    />
  );

  if (composing) {
    return letters;
  }

  return (
    <PatientTabFrame
      title={t('patients.tabs.medcom')}
      count={count}
      action={
        <div className="flex flex-wrap items-center gap-s2">
          {canWriteGgop && onGgop ? (
            <Button type="button" variant="secondary" onClick={onGgop}>
              {t('patients.ggop.open')}
            </Button>
          ) : null}
          <Button type="button" onClick={() => createRef.current()}>
            {t('patients.medcom.new')}
          </Button>
        </div>
      }
    >
      <div className="mt-s4 space-y-s5 [&_section]:rounded-none [&_section]:border-0 [&_section]:p-0">
        {canViewReferrals ? (
          <>
            <ReceivedEpikriserPanel clinicId={clinicId} patientId={patientId} canAttach={canAttach} />
            <ReceivedBookingsvarPanel clinicId={clinicId} patientId={patientId} canAttach={canAttach} />
            {canViewGgop ? (
              <ReceivedGgopPanel clinicId={clinicId} patientId={patientId} canRevealCpr={canRevealCpr} />
            ) : null}
            {canViewXdis ? (
              <ReceivedXdis16Panel
                clinicId={clinicId}
                patientId={patientId}
                patientName={patientName}
                canRevealCpr={canRevealCpr}
              />
            ) : null}
            {canViewXdis ? (
              <XdisFlowPanel
                clinicId={clinicId}
                patientId={patientId}
                patientName={patientName}
                canRevealCpr={canRevealCpr}
              />
            ) : null}
          </>
        ) : null}
        {letters}
      </div>
    </PatientTabFrame>
  );
}
